/**
 * Interview Completed Page
 * Shows processing status while assessment is being analyzed
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "@/store/hooks";
import {
  selectProgressMessage,
  selectIsPolling,
  selectAssessmentId,
  selectCurrentStep,
  selectCompletedSteps,
} from "@/store/selectors/interviewAssessmentSelectors";
import { useAssessmentPolling } from "@/hooks/useAssessmentPolling";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { MeetingSessionManager } from "@/utils/meetingSessionManager";
import { logger } from "@/utils/logger";
import { useProduct } from "@/hooks/useProduct";

export default function InterviewCompleted() {
  const config = useProduct();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  // Get meetingId from URL params
  const meetingId = searchParams.get("meetingId");

  // Get meeting session directly (not in state to avoid timing issues)
  const meetingSession = meetingId
    ? MeetingSessionManager.get(meetingId)
    : null;

  // Redux state
  const assessmentId = useAppSelector(selectAssessmentId);
  const progressMessage = useAppSelector(selectProgressMessage);
  const isPolling = useAppSelector(selectIsPolling);
  const currentStep = useAppSelector(selectCurrentStep);
  const completedSteps = useAppSelector(selectCompletedSteps);

  // Check for session error on mount
  useEffect(() => {
    if (meetingId && !meetingSession) {
      logger.error(
        "[InterviewCompleted] No meeting session found for:",
        meetingId
      );
      toast({
        title: config.i18n.interviewCompleted.sessionError,
        description: config.i18n.interviewCompleted.sessionNotFound,
        variant: "destructive",
      });
      setTimeout(() => navigate("/dashboard"), 2000);
    } else if (meetingSession) {
      logger.log("[InterviewCompleted] Got meeting session:", meetingSession);
    }
  }, [
    meetingId,
    meetingSession,
    navigate,
    toast,
    config.i18n.interviewCompleted,
  ]);

  // Polling hook
  const { startPolling } = useAssessmentPolling({
    meetingSession: meetingSession || undefined,
    onCompleted: () => {
      logger.log(
        "[InterviewCompleted] Assessment completed, navigating to feedback"
      );
      // Wait 3s before navigating
      setTimeout(() => {
        navigate(`/meeting/${meetingId}/feedback/${assessmentId}`);
      }, 3000);
    },
    onError: (error) => {
      logger.error("[InterviewCompleted] Polling error:", error);
      toast({
        title: config.i18n.interviewCompleted.processingError,
        description: error,
        variant: "destructive",
      });
    },
    onTimeout: () => {
      logger.warn("[InterviewCompleted] Polling timeout");
      toast({
        title: config.i18n.interviewCompleted.processingTakingLonger,
        description: config.i18n.interviewCompleted.processingTakingLongerDesc,
        variant: "default",
      });
      // Navigate to dashboard after timeout
      setTimeout(() => {
        navigate("/dashboard");
      }, 3000);
    },
    autoStart: true, // Auto-start when component mounts
  });

  // Redirect if no assessment ID
  useEffect(() => {
    if (!assessmentId) {
      logger.warn("[InterviewCompleted] No assessment ID found, redirecting");
      toast({
        title: config.i18n.interviewCompleted.noAssessmentFound,
        description: config.i18n.interviewCompleted.noAssessmentFoundDesc,
        variant: "destructive",
      });
      navigate("/dashboard");
    }
  }, [assessmentId, navigate, toast, config.i18n.interviewCompleted]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5">
      {/* Header */}
      <header className="border-b border-border/40 backdrop-blur-sm bg-background/80 sticky top-0 z-10">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {config.i18n.interviewCompleted.backToDashboard}
          </Button>
          <h1 className="text-lg font-semibold">
            {config.i18n.interviewCompleted.title}
          </h1>
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Status Card */}
          <Card className="p-8 border-border/50 backdrop-blur-sm bg-card/80">
            <div className="space-y-8">
              {/* Icon */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                    {completedSteps.length < 4 ? (
                      <Loader2 className="w-12 h-12 text-primary animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-12 h-12 text-green-500" />
                    )}
                  </div>
                  {/* Pulse animation */}
                  {completedSteps.length < 4 && (
                    <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
                  )}
                </div>
              </div>

              {/* Title */}
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-foreground">
                  {completedSteps.length < 4
                    ? config.i18n.interviewCompleted.analyzing
                    : config.i18n.interviewCompleted.complete}
                </h2>
                <p className="text-muted-foreground">
                  {completedSteps.length < 4
                    ? config.i18n.interviewCompleted.description
                    : "Your feedback is ready to view!"}
                </p>
              </div>

              {/* Progress Message */}
              {/* <div className="bg-muted/20 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground animate-pulse">
                  {progressMessage}
                </p>
              </div> */}

              {/* Progress Steps */}
              <div className="space-y-3">
                {[
                  config.i18n.interviewCompleted.stepFlow,
                  config.i18n.interviewCompleted.stepSkills,
                  config.i18n.interviewCompleted.stepFeedback,
                  config.i18n.interviewCompleted.stepReport,
                ].map((step, index) => {
                  const isCompleted = completedSteps.includes(index);
                  const isActive = currentStep === index && !isCompleted;

                  return (
                    <div key={index} className="flex items-center gap-3">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-500 ${
                          isCompleted
                            ? "bg-green-500/20 text-green-500"
                            : isActive
                              ? "bg-primary/20 text-primary animate-pulse"
                              : "bg-muted/40 text-muted-foreground"
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span
                        className={`text-sm transition-colors duration-500 ${
                          isCompleted
                            ? "text-foreground font-medium"
                            : isActive
                              ? "text-primary"
                              : "text-muted-foreground"
                        }`}
                      >
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Info */}
              <div className="text-center text-xs text-muted-foreground">
                This usually takes 10-20 seconds. Please don't close this page.
              </div>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
